import { addDefaultIfEmpty, insertAfterIndex, removeAtIndex, sum, updateAtIndex, updateWhere } from '../../helpers/array'
import { uniqueId } from '../../helpers/uniqueId'

export type Overhead = {
  id: string,
  description: string,
  annualCost: number,
}

export type OverheadGroup = {
  groupName: string,
  overhead: Overhead[]
}

export const emptyOverhead = (): Overhead => ({id: uniqueId(), description: '', annualCost: 0})

export const createDefaultOverhead = (): OverheadGroup[] => [
  {
    groupName: 'Advertising & Marketing', 
    overhead: [
      { ...emptyOverhead(), description: 'Website' },
      { ...emptyOverhead(), description: 'Advertising' },
      { ...emptyOverhead(), description: 'Uniforms' },
      { ...emptyOverhead(), description: 'Client gifts' },
    ]
  },
  {
    groupName: 'Bank & Financial Charges', 
    overhead: [
      { ...emptyOverhead(), description: 'Bank fees' },
      { ...emptyOverhead(), description: 'Credit card processing' },
      { ...emptyOverhead(), description: 'Interest expense' },
      { ...emptyOverhead(), description: 'Taxes & licenses' },
    ]
  },
  {
    groupName: 'Equipment & Truck', 
    overhead: [
      { ...emptyOverhead(), description: 'Equipment maintenance' },
      { ...emptyOverhead(), description: 'Truck maintenance' },
      { ...emptyOverhead(), description: 'Fuel' },
      { ...emptyOverhead(), description: 'Rentals' },
    ]
  },
  {
    groupName: 'Donations & Charity',
    overhead: [
      { ...emptyOverhead(), description: 'Local sponsorships' },
      { ...emptyOverhead(), description: 'Donations' },
      { ...emptyOverhead(), description: 'Charity' },
    ]
  },
  {
    groupName: 'Overhead Staff',
    overhead: [
      { ...emptyOverhead(), description: 'Owner\'s salary' },
      { ...emptyOverhead(), description: 'Sales staff' },
      { ...emptyOverhead(), description: 'Marketing staff' },
      { ...emptyOverhead(), description: 'Office admin' },
      { ...emptyOverhead(), description: 'Payroll service' },
      { ...emptyOverhead(), description: 'Payroll taxes' },
      { ...emptyOverhead(), description: 'Gifts/bonuses' },
    ]
  },
  {
    groupName: 'Professional Services',
    overhead: [
      { ...emptyOverhead(), description: 'Consulting' },
      { ...emptyOverhead(), description: 'Accountant' },
      { ...emptyOverhead(), description: 'Insurance' },
      { ...emptyOverhead(), description: 'Professional fees' }
    ]
  },
  {
    groupName: 'Small Equipment',
    overhead: [
      { ...emptyOverhead(), description: 'Job trailer tools' },
      { ...emptyOverhead(), description: 'Small engine equipment' },
    ]
  },
  {
    groupName: 'Subscriptions & Dues',
    overhead: [
      { ...emptyOverhead(), description: 'Subscriptions' },
      { ...emptyOverhead(), description: 'Postage & delivery' },
    ]
  },
  {
    groupName: 'Travel',
    overhead: [
      { ...emptyOverhead(), description: 'Meetings/training' },
      { ...emptyOverhead(), description: 'Trade shows/events' },
    ]
  },
  {
    groupName: 'Utilities',
    overhead: [
      { ...emptyOverhead(), description: 'Internet' },
      { ...emptyOverhead(), description: 'Cell phones' },
      { ...emptyOverhead(), description: 'Water' },
      { ...emptyOverhead(), description: 'Electricity' },
    ]
  },
  {
    groupName: 'Miscellaneous',
    overhead: [
      { ...emptyOverhead(), description: 'Garbage disposal' },
      { ...emptyOverhead(), description: 'Office supplies' },
      { ...emptyOverhead(), description: 'Shop rent' },
      { ...emptyOverhead(), description: 'Other' },
    ]
  }
]


const updateOverheadGroup = (overheadGroups: OverheadGroup[], groupName: string, updater: (overhead: Overhead[]) => Overhead[]): OverheadGroup[] =>
  updateWhere(overheadGroups, x => x.groupName === groupName, g => ({...g, overhead: updater(g.overhead)}))

export const remove = (overheadGroups: OverheadGroup[], groupName: string, index: number): OverheadGroup[] =>
  updateOverheadGroup(overheadGroups, groupName, x => addDefaultIfEmpty(removeAtIndex(x, index), emptyOverhead()))

export const add = (overheadGroups: OverheadGroup[], groupName: string, index: number): OverheadGroup[] =>
  updateOverheadGroup(overheadGroups, groupName, x => insertAfterIndex(x, index, emptyOverhead()))

export const updateDescription = (overheadGroups: OverheadGroup[], groupName: string, index: number, description: string): OverheadGroup[] =>
  updateOverheadGroup(overheadGroups, groupName, x => updateAtIndex(x, index, y => ({...y, description})))

export const updateAnnualCost = (overheadGroups: OverheadGroup[], groupName: string, index: number, annualCost: number): OverheadGroup[] =>
  updateOverheadGroup(overheadGroups, groupName, x => updateAtIndex(x, index, y => ({...y, annualCost})))

export type CardProcessingOverhead = {
  groupName: string
  index: number
  description: string
  annualCost: number
}

const CARD_PROCESSING_PATTERN = /credit[- ]?card|card processing|merchant (fee|service)|payment processing/i

export const isCardProcessingDescription = (description: string): boolean =>
  CARD_PROCESSING_PATTERN.test(description)

export const findCardProcessingOverhead = (overheadGroups: OverheadGroup[]): CardProcessingOverhead[] =>
  overheadGroups.flatMap(group => group.overhead
    .map((overhead, index) => ({ groupName: group.groupName, index, description: overhead.description, annualCost: overhead.annualCost }))
    .filter(x => isCardProcessingDescription(x.description)))

export const findChargedCardProcessingOverhead = (overheadGroups: OverheadGroup[]): CardProcessingOverhead[] =>
  findCardProcessingOverhead(overheadGroups).filter(x => x.annualCost > 0)

export const sumOverhead = (overheadGroups: OverheadGroup[]): number =>
  sum(overheadGroups.flatMap(x => x.overhead), x => x.annualCost)

export const enforceInvariants = (overheadGroups: OverheadGroup[] | undefined): OverheadGroup[] => {
  if (overheadGroups && overheadGroups.length > 0)
  {
    return overheadGroups.map(x => {
      if (x.overhead.length === 0) {
        return {...x, overhead: [emptyOverhead()]}
      }
      return x
    })
  }
  return createDefaultOverhead()
}